<template>
  <div class="game-container">
    <!-- 数据统计卡片 -->
    <el-card class="statistics-card">
      <game-statistics
        ref="gameStatistics"
        :loading="loading"
        :filter-params="filterParams">
      </game-statistics>
    </el-card>
    
    <!-- 操作按钮栏卡片 -->
    <el-card class="operation-card">
      <div class="operation-bar">
        <div class="left-buttons">
          <el-button type="primary" @click="handleAdd">
            <i class="el-icon-plus"></i> 添加
          </el-button>
          <el-button type="danger" @click="handleBatchDelete" :disabled="!selectedGames.length">
            <i class="el-icon-delete"></i> 删除
          </el-button>
          <el-button type="info" @click="handleRefresh">
            <i class="el-icon-refresh"></i> 刷新
          </el-button>
        </div>

        <!-- 查询控件 -->
        <div class="filter-container">
          <div class="left-section">
            <el-form :inline="true" :model="filterForm" class="filter-form">
              <div class="form-content">
                <el-form-item>
                  <el-input
                    v-model="filterForm.game_name"
                    placeholder="赛事名称"
                    clearable
                    @keyup.enter.native="handleSearch"
                    style="width: 200px">
                  </el-input>
                </el-form-item>
                
                <el-form-item>
                  <el-select
                    v-model="filterForm.game_type"
                    placeholder="赛事类型"
                    clearable
                    style="width: 120px">
                    <el-option
                      v-for="item in dictData.game_type"
                      :key="item.dict_value"
                      :label="item.dict_value"
                      :value="item.dict_value">
                    </el-option>
                  </el-select>
                </el-form-item>
                
                <el-form-item>
                  <el-select
                    v-model="filterForm.game_level"
                    placeholder="赛事级别"
                    clearable
                    style="width: 120px">
                    <el-option
                      v-for="item in dictData.game_level"
                      :key="item.dict_value"
                      :label="item.dict_value"
                      :value="item.dict_value">
                    </el-option>
                  </el-select>
                </el-form-item>
                
                <el-form-item>
                  <el-select
                    v-model="filterForm.fix_status"
                    placeholder="订正状态"
                    clearable
                    style="width: 120px">
                    <el-option label="已完成" value="已完成"></el-option>
                    <el-option label="未完成" value="未完成"></el-option>
                    <el-option label="未添加" value="未添加"></el-option>
                  </el-select>
                </el-form-item>
                
                <el-form-item>
                  <el-select
                    v-model="filterForm.replay_status"
                    placeholder="复盘状态"
                    clearable
                    style="width: 120px">
                    <el-option label="已完成" value="已完成"></el-option>
                    <el-option label="未完成" value="未完成"></el-option>
                  </el-select>
                </el-form-item>
                
                <el-form-item>
                  <el-date-picker
                    v-model="filterForm.game_time"
                    type="daterange"
                    range-separator="至"
                    start-placeholder="开始日期"
                    end-placeholder="结束日期"
                    value-format="yyyy-MM-dd"
                    style="width: 260px">
                  </el-date-picker>
                </el-form-item>
                
                <el-form-item class="filter-buttons">
                  <el-button @click="handleReset">重置</el-button>
                  <el-button type="primary" @click="handleSearch">查询</el-button>
                </el-form-item>
              </div>
            </el-form>
          </div>
        </div>
      </div>
    </el-card>
    
    <!-- 赛事数据表格卡片 -->
    <el-card class="table-card">
      <!-- 赛事列表 -->
      <game-list
        ref="gameList"
        v-model="selectedGames"
        :loading="loading"
        :game-list="sortedAndPagedList"
        :questions-cache="questionsCache"
        :current-page="currentPage"
        :page-size="pageSize"
        :total="total"
        @selection-change="handleSelectionChange"
        @sort-change="handleSortChange"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        @add-question="handleAddQuestion"
        @question-operation-complete="handleQuestionOperationComplete"
        @replay="handleReplay"
        @edit="handleEdit">
      </game-list>
    </el-card>
    
    <!-- 版权信息卡片 -->
    <el-card class="copyright-card">
      <div class="copyright-content">
        <span>Copyright © 2024 OIer之路 All rights reserved.  |  闽ICP备2025095302号-1</span>
      </div>
    </el-card>

    <!-- 赛事对话框 -->
    <game-dialog
      ref="gameDialog"
      :visible.sync="dialogVisible"
      :game="currentGame"
      @success="handleSuccess">
    </game-dialog>

    <!-- 题目录入对话框 -->
    <question-dialog
      ref="questionDialog"
      :visible.sync="questionDialogVisible"
      :game-code="currentGameCode"
      :question="currentQuestion"
      @success="handleQuestionRefresh">
    </question-dialog>
    
    <!-- 题目列表抽屉 -->
    <el-drawer
      :visible.sync="drawerVisible"
      direction="rtl"
      size="50%"
      :before-close="handleDrawerClose">
      <template slot="title">
        <span>{{ currentGame ? currentGame.game_name : '' }} - 题目列表</span>
      </template>
      <div class="drawer-content">
        <question-list
          v-if="drawerVisible"
          ref="questionList"
          :game-code="currentGame ? currentGame.game_code : ''"
          @refresh-complete="handleQuestionRefreshComplete"
          @edit="handleQuestionEdit"
          @delete="handleQuestionDelete"
          @collect="handleQuestionCollect"
          :operation-width="125">
        </question-list>
      </div>
    </el-drawer>

    <!-- 复盘组件 -->
    <game-replay
      v-if="currentGame"
      :visible.sync="replayDialogVisible"
      :game-info="currentGame"
      @close="handleReplayClose"
      @success="handleReplaySuccess"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :append-to-body="true"
      :destroy-on-close="true"
      :modal-append-to-body="true"
    />

    <!-- 标签选择对话框 -->
    <el-dialog
      title="选择标签"
      :visible.sync="tagDialogVisible"
      append-to-body
      width="30%">
      <el-select
        v-model="selectedTagCodes"
        multiple
        filterable
        placeholder="请选择标签"
        style="width: 100%">
        <el-option
          v-for="tag in allTags"
          :key="tag.tag_code"
          :label="tag.tag_name"
          :value="tag.tag_code">
        </el-option>
      </el-select>
      <div slot="footer" class="dialog-footer">
        <el-button @click="tagDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleTagSelect">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getGameList, deleteGame, batchDeleteGames, getFilterOptions, saveGameReplay, deleteGameReplay } from '@/api/game'
import { getQuestions, updateQuestionCollect } from '@/api/question'
import { getDictData } from '@/api/dict'
import GameList from './components/GameList.vue'
import GameDialog from './components/GameDialog.vue'
import QuestionList from './components/QuestionList.vue'
import GameStatistics from './components/GameStatistics.vue'
import QuestionDialog from './components/QuestionDialog.vue'
import GameReplay from './components/GameReplay.vue'
import { getAllTags } from '@/api/tag'

export default {
  name: 'Game',
  components: {
    GameList,
    GameDialog,
    QuestionList,
    GameStatistics,
    QuestionDialog,
    GameReplay
  },
  data() {
    return {
      loading: false,
      gameList: [], // 当前页显示的数据
      currentPage: 1,
      pageSize: 20,
      total: 0,
      sortProp: 'game_time',  // 默认按赛事时间排序
      sortOrder: 'descending',  // 默认倒序
      selectedGames: [],
      currentGame: null,
      currentQuestion: null,
      dialogVisible: false,
      questionDialogVisible: false,
      drawerVisible: false,
      currentGameCode: '',
      questionsCache: new Map(),
      filterParams: {},
      replayDialogVisible: false,
      currentReplayCode: '',
      tagDialogVisible: false,
      selectedTagCodes: [],
      replayForm: {
        questions: []  // 每个题目的复盘信息
      },
      gameQuestions: [], // 当前赛事的题目列表
      replayRules: {
        'questions.*.is_expect': [
          { required: true, message: '请选择是否符合预期', trigger: 'change' }
        ],
        'questions.*.is_plan': [
          { required: true, message: '请选择是否需要补充知识点', trigger: 'change' }
        ],
        'questions.*.replay_info': [
          { required: true, message: '请输入复盘内容', trigger: 'blur' }
        ]
      },
      allTags: [],
      // 新增查询表单数据
      filterForm: {
        game_name: '',
        game_type: '',
        game_level: '',
        fix_status: '',
        replay_status: '',
        game_time: []
      },
      // 新增字典数据
      dictData: {
        game_type: [],
        game_level: [],
        game_scale: [],
        game_org: []
      }
    }
  },
  computed: {
    sortedAndPagedList() {
      // 直接返回服务端排序后的数据
      return this.gameList;
    }
  },
  async created() {
    await this.loadDictData()
    this.handleSearch()
  },
  methods: {
    // 加载字典数据
    async loadDictData() {
      try {
        const dictKeys = ['game_type', 'game_level', 'game_scale', 'game_org']
        const promises = dictKeys.map(key => getDictData(key))
        const results = await Promise.all(promises)
        
        results.forEach((res, index) => {
          if (res.code === 200) {
            this.dictData[dictKeys[index]] = res.data
          } else {
            console.error(`加载字典数据失败 (${dictKeys[index]}):`, res.message)
          }
        })
      } catch (error) {
        console.error('加载字典数据失败:', error)
        this.$message.error('加载字典数据失败')
      }
    },
    
    // 获取查询参数
    getQueryParams() {
      const params = {
        pageNum: this.currentPage,
        pageSize: this.pageSize,
        sort_prop: this.sortProp,
        sort_order: this.sortOrder
      };
      
      // 添加查询参数
      if (this.filterForm.game_name) {
        params.game_name = this.filterForm.game_name;
      }
      
      if (this.filterForm.game_type) {
        params.game_type = this.filterForm.game_type;
      }
      
      if (this.filterForm.game_level) {
        params.game_level = this.filterForm.game_level;
      }
      
      if (this.filterForm.fix_status) {
        params.fix_status = this.filterForm.fix_status;
      }
      
      if (this.filterForm.replay_status) {
        params.replay_status = this.filterForm.replay_status;
      }
      
      if (this.filterForm.game_time && this.filterForm.game_time.length === 2) {
        params.game_time_start = this.filterForm.game_time[0];
        params.game_time_end = this.filterForm.game_time[1];
      }
      
      return params;
    },
    
    // 处理查询
    async handleSearch() {
      try {
        this.loading = true
        const params = this.getQueryParams()
        
        // 更新 filterParams，用于统计组件
        const newFilterParams = {
          game_name: this.filterForm.game_name,
          game_type: this.filterForm.game_type,
          game_level: this.filterForm.game_level,
          fix_status: this.filterForm.fix_status,
          replay_status: this.filterForm.replay_status,
          game_time_start: this.filterForm.game_time?.[0] || '',
          game_time_end: this.filterForm.game_time?.[1] || ''
        }
        
        // 使用整个对象替换以确保响应式更新
        this.filterParams = newFilterParams
        
        console.log('Updated filterParams:', this.filterParams)
        
        const res = await getGameList(params)
        if (res.code === 200) {
          this.gameList = res.data.items || []
          this.total = res.data.total
        } else {
          console.error('查询失败:', res.msg)
          this.$message.error(res.msg || '查询失败')
          return
        }
      } catch (error) {
        console.error('查询失败:', error)
        this.$message.error(error.message || '查询失败')
      } finally {
        this.loading = false
      }
    },
    
    // 重置查询
    handleReset() {
      this.filterForm = {
        game_name: '',
        game_type: '',
        game_level: '',
        fix_status: '',
        replay_status: '',
        game_time: []
      }
      this.currentPage = 1
      this.pageSize = 20
      this.sortProp = 'game_time'
      this.sortOrder = 'descending'
      
      // 重置 filterParams
      this.filterParams = {}
      
      // 执行查询
      this.handleSearch()
    },
    
    // 获取所有数据
    async fetchAllData() {
      try {
        this.loading = true
        const params = this.getQueryParams()
        
        const res = await getGameList(params)
        if (res.code === 200) {
          this.gameList = res.data.items || []
          this.total = res.data.total
          // 更新统计数据
          if (this.$refs.gameStatistics && typeof this.$refs.gameStatistics.refresh === 'function') {
            this.$refs.gameStatistics.refresh()
          }
        } else {
          console.error('获取赛事列表失败:', res.msg)
          this.$message.error(res.msg || '获取赛事列表失败')
          return
        }
      } catch (error) {
        console.error('获取赛事列表失败:', error)
        this.$message.error(error.message || '获取赛事列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 处理排序变化
    handleSortChange({ prop, order }) {
      console.log('Sort changed:', { prop, order });
      this.sortProp = prop || 'game_time';
      this.sortOrder = order || 'descending';
      // 重新从服务端获取排序后的数据
      this.handleSearch();
    },
    
    // 处理页码变化
    handleCurrentChange(val) {
      this.currentPage = val
      this.handleSearch()
    },
    
    // 处理每页条数变化
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.handleSearch()
    },
    
    // 刷新数据
    handleRefresh() {
      this.fetchAllData()
    },
    
    handleSelectionChange(selection) {
      this.selectedGames = selection
    },
    
    handleClearFilter() {
      this.fetchAllData()
    },
    
    handleAdd() {
      this.currentGame = null
      this.dialogVisible = true
    },
    
    handleEdit(row) {
      try {
        console.log('编辑赛事:', row)
        if (row) {
          // 来自 GameList 组件的编辑事件
          this.currentGame = JSON.parse(JSON.stringify(row)) // 深拷贝确保不影响原数据
          this.dialogVisible = true
        } else {
          // 来自工具栏的编辑按钮
          if (this.selectedGames.length !== 1) {
            this.$message.warning('请选择一条记录进行编辑')
            return
          }
          this.currentGame = JSON.parse(JSON.stringify(this.selectedGames[0])) // 深拷贝确保不影响原数据
          this.dialogVisible = true
        }
      } catch (error) {
        console.error('处理编辑事件失败:', error)
        this.$message.error('编辑操作失败')
      }
    },
    
    handleSuccess() {
      this.dialogVisible = false
      this.fetchAllData()
    },
    
    handleAddQuestion(gameInfo) {
      this.currentGameCode = gameInfo.game_code
      this.currentQuestion = null // 清空当前题目数据
      this.questionDialogVisible = true
    },
    
    handleQuestionEdit(question) {
      this.currentGameCode = question.game_code
      this.currentQuestion = question
      this.questionDialogVisible = true
    },
    
    async handleQuestionRefresh(question) {
      try {
        console.log('开始刷新所有相关数据')
        
        // 使用与其他操作相同的刷新机制
        await this.handleQuestionOperationComplete({
          gameCode: question.game_code,
          operation: 'add'
        })
        
        this.questionDialogVisible = false
        console.log('所有数据刷新完成')
      } catch (error) {
        // 只记录错误，不显示提示
        console.error('刷新数据时出现错误:', error)
      }
    },
    
    async refreshAllData() {
      try {
        console.log('开始刷新所有数据')
        await this.fetchAllData()
        
        // 刷新统计数据
        if (this.$refs.gameStatistics) {
          await this.$refs.gameStatistics.refresh()
        }
        
        console.log('所有数据刷新完成')
      } catch (error) {
        console.error('刷新数据失败:', error)
        this.$message.error('刷新数据失败')
      }
    },
    
    async handleBatchDelete() {
      if (!this.selectedGames.length) {
        this.$message.warning('请选择要删除的记录')
        return
      }
      
      try {
        await this.$confirm('确认删除选中赛事？', '提示', {
          type: 'warning'
        })
        
        const ids = this.selectedGames.map(game => game.id)
        const res = await batchDeleteGames({ ids })
        
        if (res.code === 200) {
          // 删除相关的复盘记录
          for (const game of this.selectedGames) {
            try {
              await deleteGameReplay(game.game_code)
            } catch (error) {
              console.warn('删除复盘记录失败:', error)
            }
          }
          
          this.$message.success('删除成功')
          // 刷新数据
          await this.fetchAllData()
          // 清空选中
          this.selectedGames = []
          // 刷新统计
          if (this.$refs.gameStatistics) {
            await this.$refs.gameStatistics.refresh()
          }
        } else {
          this.$message.error(res.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    handleDrawerClose(done) {
      done()
    },

    toggleColumnSelector() {
      if (this.$refs.gameList) {
        this.$refs.gameList.toggleColumnSelector()
      }
    },

    async handleReplay(row) {
      console.log('处理复盘事件:', row)
      try {
        if (!row || !row.game_code) {
          console.error('无效的赛事数据:', row)
          this.$message.error('无效的赛事数据')
          return
        }

        // 获取题目信息
        const res = await getQuestions(row.game_code)
        if (res.code === 200) {
          const questions = res.data || []
          if (questions.length > 0) {
            const firstQuestion = questions[0]
            // 设置当前赛事信息
            this.currentGame = {
              ...row,
              question_seq: firstQuestion.question_seq || 'T1',
              question_score: firstQuestion.question_score || '0',
              question_score_fix: firstQuestion.question_score_fix || '0',
              question_name: firstQuestion.question_name || '',
              tags: firstQuestion.tags || []
            }
          } else {
            this.$message.warning('未找到题目信息')
            return
          }
        } else {
          this.$message.error(res.message || '获取题目列表失败')
          return
        }
        
        console.log('当前赛事信息:', this.currentGame)
        
        // 显示复盘对话框
        this.replayDialogVisible = true
        console.log('复盘对话框显示状态:', this.replayDialogVisible)
      } catch (error) {
        console.error('处理复盘操作时出错:', error)
        this.$message.error('复盘操作失败')
      }
    },

    handleReplayClose() {
      console.log('复盘对话框关闭')
      this.replayDialogVisible = false
      this.currentGame = null
    },

    async handleReplaySuccess() {
      console.log('复盘保存成功')
      await this.fetchAllData()
      this.$message.success('保存成功')
    },

    async fetchGameQuestions(gameCode) {
      try {
        const res = await getQuestions(gameCode)
        if (res.code === 200) {
          const questions = res.data || []
          if (questions.length > 0) {
            const firstQuestion = questions[0]
            this.currentGame = {
              ...this.currentGame,
              question_seq: firstQuestion.question_seq || 'T1',
              question_score: firstQuestion.question_score || '0',
              question_score_fix: firstQuestion.question_score_fix || '0',
              question_name: firstQuestion.question_name || '',
              tags: firstQuestion.tags || []
            }
          }
        } else {
          this.$message.error(res.message || '获取题目列表失败')
        }
      } catch (error) {
        console.error('获取题目列表失败:', error)
        this.$message.error('获取题目列表失败')
      }
    },

    async handleReplaySubmit() {
      try {
        // 表单验证
        await this.$refs.replayForm.validate()
        
        // 构造提交的数据
        const submitData = {
          game_code: this.currentGame.game_code,
          questions: this.replayForm.questions.map(question => ({
            question_code: question.question_code,
            is_expect: Number(question.is_expect),  // 确保是数字类型
            is_plan: Number(question.is_plan),      // 确保是数字类型
            replay_info: question.replay_info.trim() // 去除首尾空格
          }))
        }
        
        console.log('准备提交的复盘数据:', submitData)
        
        // 调用保存API
        console.log('开始调用保存API...')
        const res = await saveGameReplay(submitData)
        console.log('API返回结果:', res)
        
        if (res.code === 200) {
          this.$message.success('保存成功')
          console.log('保存成功，开始刷新数据...')
          // 刷新赛事列表
          await this.fetchAllData()
          // 关闭对话框
          this.handleReplayClose()
        } else {
          console.error('保存失败，API返回:', res)
          this.$message.error(res.message || '保存失败')
        }
      } catch (error) {
        console.error('保存复盘数据失败:', error)
        this.$message.error('保存失败')
      }
    },

    showTagDialog() {
      this.tagDialogVisible = true
    },

    handleTagSelect() {
      this.replayForm.tags = this.selectedTagCodes.map(code => {
        return { tag_code: code, tag_name: this.allTags.find(tag => tag.tag_code === code).tag_name }
      })
      this.tagDialogVisible = false
    },

    handleTagClose(tag) {
      this.replayForm.tags = this.replayForm.tags.filter(t => t.tag_code !== tag.tag_code)
    },

    // 处理题目操作完成事件
    async handleQuestionOperationComplete(data) {
      try {
        this.loading = true
        console.log('开始刷新所有相关数据:', data)
        
        // 1. 刷新题目列表（通过 GameList 组件）
        if (this.$refs.gameList) {
          await this.$refs.gameList.handleQuestionRefreshComplete(data.gameCode)
        }
        
        // 2. 刷新赛事列表数据
        await this.fetchAllData()
        
        // 3. 刷新统计数据
        if (this.$refs.gameStatistics && typeof this.$refs.gameStatistics.refresh === 'function') {
          try {
            await this.$refs.gameStatistics.refresh()
          } catch (err) {
            console.warn('统计数据刷新失败，但不影响主要功能:', err)
          }
        }
        
        console.log('所有数据刷新完成')
      } catch (error) {
        console.error('刷新数据时出现错误:', error)
        this.$message.error('刷新数据失败')
      } finally {
        this.loading = false
      }
    },

    // 处理题目收藏
    async handleQuestionCollect({ questionCode, isCollect }) {
      try {
        const res = await updateQuestionCollect(questionCode, isCollect)
        if (res.code === 200) {
          // 更新本地状态
          const questions = this.$refs.questionList.questions
          const question = questions.find(q => q.question_code === questionCode)
          if (question) {
            question.is_collect = isCollect
          }
          this.$message.success(isCollect ? '收藏成功' : '取消收藏成功')
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (error) {
        console.error('收藏操作失败:', error)
        this.$message.error('操作失败')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.game-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  background-color: #f0f2f5;
  overflow: hidden;
  
  .statistics-card {
    margin: 8px 0 0 0;
    flex-shrink: 0;
    background-color: #fff;
    border-radius: 4px;
    height: 110px;
    position: relative;
    z-index: 10;
    
    :deep(.el-card__body) {
      height: 100%;
      display: flex;
      align-items: center;
    }
  }
  
  .operation-card {
    flex-shrink: 0;
    margin: 8px 0 0 0;
    background-color: #fff;
    border-radius: 4px;
    position: relative;
    z-index: 10;
    
    .operation-bar {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid #EBEEF5;
      
      .left-buttons {
        display: flex;
        gap: 7px;
        flex-shrink: 0;
        
        .el-button {
          height: 35px;
          line-height: 33px;
          padding: 0 15px;
          min-width: 50px;
          font-size: 13px;
          
          &.el-button--primary {
            background-color: #7B9DF3;
            border-color: #7B9DF3;
          }
          
          &.el-button--success {
            background-color: #67C99B;
            border-color: #67C99B;
          }
          
          &.el-button--danger {
            background-color: #F48989;
            border-color: #F48989;
          }
          
          &.el-button--info {
            background-color: #8B8FA3;
            border-color: #8B8FA3;
          }
        }
      }
      
      .filter-container {
        flex: 1;
        margin-left: 16px;
        overflow: hidden;
        
        .left-section {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 1;
          overflow-x: auto;
          height: 100%;
          
          /* 自定义滚动条样式 */
          &::-webkit-scrollbar {
            height: 5px;
          }
          
          &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          
          &::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          
          &::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        }
        
        .filter-form {
          display: flex;
          align-items: center;
          white-space: nowrap;
          height: 100%;
          
          .form-content {
            display: flex;
            align-items: center;
            gap: 8px;
            height: 100%;
          }
          
          .el-form-item {
            margin-bottom: 0;
            margin-right: 0;
            display: flex;
            align-items: center;
            
            &.filter-buttons {
              margin-left: auto;
              white-space: nowrap;
              position: sticky;
              right: 0;
              background-color: #fff;
              padding-left: 8px;
              z-index: 1;
              
              .el-button {
                height: 35px;
                line-height: 33px;
                padding: 0 15px;
                font-size: 13px;
                
                & + .el-button {
                  margin-left: 8px;
                }
              }
            }
            
            :deep {
              .el-input,
              .el-select,
              .el-date-editor {
                width: 100%;
                
                .el-input__inner {
                  height: 35px;
                  line-height: 35px;
                  font-size: 13px;
                  padding: 0 10px;
                }
                
                .el-range__icon,
                .el-range__close-icon,
                .el-input__icon {
                  height: 35px;
                  line-height: 35px;
                  top: 0;
                }
                
                .el-range-separator {
                  height: 35px;
                  line-height: 35px;
                  font-size: 13px;
                  padding: 0;
                }
                
                .el-range-input {
                  height: 33px;
                  line-height: 33px;
                  font-size: 13px;
                  margin: 1px 0;
                }
              }
              
              .el-date-editor.el-input,
              .el-date-editor.el-input__wrapper {
                height: 35px;
                line-height: 35px;
                padding: 0 10px;
              }
              
              .el-range-editor.el-input__inner {
                height: 35px;
                line-height: 35px;
                padding: 0 10px;
                display: flex;
                align-items: center;
              }
              
              .el-select .el-input {
                height: 35px;
                line-height: 35px;
                
                .el-input__inner {
                  height: 35px;
                  line-height: 35px;
                }
              }
            }
          }
        }
      }
    }
  }
  
  .table-card {
    margin: 8px 0 0 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: #fff;
    border-radius: 4px;
    min-height: 0;
    
    :deep(.el-card__body) {
      height: 100%;
      padding: 0;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    
    :deep(.el-table) {
      flex: 1;
      height: 100%;
      
      .el-table__header-wrapper {
        position: sticky;
        top: 0;
        z-index: 2;
        background-color: #fff;
      }
      
      .el-table__body-wrapper {
        overflow-y: auto;
        height: calc(100% - 40px);
      }
    }
  }
  
  .pagination-container {
    margin: 8px 16px;
    padding: 8px 16px;
    background-color: #fff;
    border-radius: 4px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-shrink: 0;
  }
  
  .copyright-card {
    margin: 8px 0;
    flex-shrink: 0;
    background-color: #fff;
    border-radius: 4px;
    height: 40px;
    
    :deep(.el-card__body) {
      height: 100%;
      padding: 0;
    }
    
    .copyright-content {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #909399;
      font-size: 14px;
    }
  }
}

::v-deep .el-drawer__header {
  margin-bottom: 0;
  padding: 16px 20px;
  border-bottom: 1px solid #dcdfe6;
  
  span {
    font-size: 16px;
    font-weight: 500;
  }
}

::v-deep .el-drawer__body {
  padding: 0;
  overflow: hidden;
}

.drawer-content {
  height: 100%;
  overflow: auto;
}

::v-deep .el-card__body {
  padding: 0;
  height: 100%;
}

.replay-dialog {
  .el-dialog {
    margin-top: 15vh !important;
  }
}
</style>